import { useState, useRef, createContext, useEffect } from 'react';
import L from 'leaflet';
import { isMobile } from 'react-device-detect';
import cx from 'classnames';

import {
  useAppSelector,
  useAppDispatch,
  canEditSelector,
  orderDescriptionSelector,
  isEmbeddedSelector,
  mapSelector,
  saveState,
  loadState,
  replaceStore,
  preferencesSelector,
  apiSavingStatusSelector,
} from './store';

import eventBus from './eventBus';
import { Map } from './components/Map';
import { Modal } from './components/spaces/Modal';
import { Sidebar } from './components/Sidebar';
import { EditTools } from './components/EditTools';
import { ThemeSidebar } from './components/ThemeSidebar';
import { WeatherSidebar } from './components/WeatherSidebar';
import { Flash } from './components/Flash';


import './App.scss';
import './tailwind.css';

export interface LeafletContextType {
  mapRef: React.MutableRefObject<L.Map | null>;
  activeMarkerRef: React.MutableRefObject<Record<string, L.Marker>>;
  activeLayerRef: React.MutableRefObject<{
    object: L.TileLayer | L.ImageOverlay | undefined;
    id: number | null;
  }>;
  hoveredMarker: string | null;
  setHoveredMarker: React.Dispatch<React.SetStateAction<string>>;
}

export const LeafletContext = createContext<LeafletContextType>({} as LeafletContextType);

export default function App({
  setComponentLoaded,
}: {
  setComponentLoaded: () => void,
}) {
  const dispatch = useAppDispatch();
  const iMap = useAppSelector(mapSelector);
  const activeMarkerRef = useRef({});
  const mapRef = useRef(null);
  const activeLayerRef = useRef({ object: undefined, id: null });
  const [hoveredMarker, setHoveredMarker] = useState('');

  const isEmbedded = useAppSelector(isEmbeddedSelector);
  const orderDescription = useAppSelector(orderDescriptionSelector);
  const preferences = useAppSelector(preferencesSelector);

  const showAdmin = useAppSelector(canEditSelector) && !isMobile && !isEmbedded;
  const isSaving = useAppSelector(apiSavingStatusSelector) === 'saving';

  // Show sidebar based on preferences - admins can still hide it via theme settings
  const showSidebar = preferences.sidebarEnabled;

  const [isSidebarOpen, setIsSidebarOpen] = useState(
    isMobile ? preferences.sidebarOpenOnLoadMobile : preferences.sidebarOpenOnLoad
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isThemeSidebarOpen, setIsThemeSidebarOpen] = useState(false);

  const hasWeatherCoords = (preferences.weatherCoords ?? '').trim().length > 0;
  const [isWeatherSidebarOpen, setIsWeatherSidebarOpen] = useState(false);

  if (!isEmbedded && orderDescription) {
    document.title = `Visrez Interactive - ${orderDescription}`;
  }

  // Draft recovery/discard functionality
  useEffect(() => {
    const stateID = `${iMap.order.id}-${iMap.id}`;
    const unsavedState = loadState(stateID);
    if (unsavedState && showAdmin) {
      const { mapUpdatedAt, ...draft } = unsavedState;
      // The map was saved after the draft was written (another tab or user),
      // so the draft may reference records that no longer exist.
      // Drafts without version are from before it was stored, so also outdated
      if (iMap.updatedAt && mapUpdatedAt !== iMap.updatedAt) {
        saveState(undefined, stateID);
        eventBus.emit('flash', 'error', 'Draft discarded: the map was saved elsewhere since it was written');
        return;
      }
      const onConfirm = () => {
        dispatch(replaceStore(draft));
        eventBus.emit('flash', 'success', 'Draft recovered');
      };
      const onCancel = () => {
        saveState(undefined, stateID);
        eventBus.emit('flash', 'success', 'Draft discarded');
      };
      eventBus.emit(
        'ask',
        'A draft has been found for this map, '
        + 'would you like to continue working on it?',
        'Recover',
        'Discard',
        onConfirm,
        onCancel,
      );
    }
    // Run on first load only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LeafletContext.Provider
      value={{
        mapRef,
        activeMarkerRef,
        activeLayerRef,
        hoveredMarker,
        setHoveredMarker,
      }}
    >
      <div id="wrapper" className={cx({
        'modal-open': isModalVisible,
        'sidebar-right': preferences.sidebarPosition === 'right',
        'sidebar-hidden': !showSidebar,
        'dark-mode': preferences.darkMode,
        'compact-mode': preferences.compactMode,
      })}>
        <div id="menu-wrapper">
          {showAdmin && (
            <EditTools
              setIsSidebarOpen={setIsSidebarOpen}
              isThemeSidebarOpen={isThemeSidebarOpen}
              setIsThemeSidebarOpen={setIsThemeSidebarOpen}
            />
          )}
          {showSidebar && <Sidebar isSidebarOpen={isSidebarOpen} />}
        </div>
        <div id="content-wrapper">
          <Flash />
          <Map
            setIsModalVisible={setIsModalVisible}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
            setComponentLoaded={setComponentLoaded}
            isWeatherSidebarOpen={isWeatherSidebarOpen}
            setIsWeatherSidebarOpen={setIsWeatherSidebarOpen}
          />
          <Modal
            isModalVisible={isModalVisible}
            setIsModalVisible={setIsModalVisible}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
        </div>

        {showAdmin && (
          <ThemeSidebar
            isOpen={isThemeSidebarOpen}
            onClose={() => setIsThemeSidebarOpen(false)}
          />
        )}

        {hasWeatherCoords && (
          <WeatherSidebar
            isOpen={isWeatherSidebarOpen}
            onClose={() => setIsWeatherSidebarOpen(false)}
          />
        )}

        {/* Block edits while saving, it would be overwritten by the saved map */}
        {showAdmin && isSaving && <div className="tw:fixed tw:inset-0 tw:z-[10000] tw:cursor-wait" />}
      </div>
    </LeafletContext.Provider>
  );
}
